/**
 * Searchable navigation entries: main categories and list pages.
 * Used by the top-bar global search to let users jump to a section.
 */

export type SearchEntry = {
    path: string
    label: string
    category: string
}

/** Backend search API result item */
export type SearchRecordResult = {
    type: string
    id: number
    label: string
    path: string
}

const RECORD_TYPE_TO_CATEGORY: Record<string, string> = {
    subscription: 'Subscriptions',
    page: 'Content pages',
    page_section: 'Page sections',
    page_content: 'Page sections',
    team_member: 'Team members',
    department: 'Departments',
    payment: 'Payments',
    newsletter: 'Email campaigns',
    email_template: 'Email templates',
    user: 'Users',
}

export function recordToSearchEntry(r: SearchRecordResult): SearchEntry {
    return {
        path: r.path,
        label: r.label,
        category: RECORD_TYPE_TO_CATEGORY[r.type] ?? r.type,
    }
}

export const searchEntries: SearchEntry[] = [
    { path: '/', label: 'Dashboard', category: 'Dashboard' },
    { path: '/account', label: 'My Account', category: 'Account' },
    { path: '/account/activity-log', label: 'Activity Log', category: 'Account' },

    { path: '/administration', label: 'Administration', category: 'Administration' },
    { path: '/administration/emails', label: 'Email Templates', category: 'Administration' },
    { path: '/administration/main-contacts', label: 'Main Contacts', category: 'Administration' },
    { path: '/administration/languages', label: 'Languages', category: 'Administration' },
    { path: '/administration/translations', label: 'Translations', category: 'Administration' },
    { path: '/administration/date-and-time', label: 'Date & Time', category: 'Administration' },
    { path: '/administration/social-media-links', label: 'Social Media', category: 'Administration' },
    { path: '/administration/currencies', label: 'Currencies', category: 'Administration' },
    { path: '/administration/api-manager', label: 'API Manager', category: 'Administration' },
    { path: '/administration/users', label: 'Users', category: 'Administration' },
    { path: '/administration/website-builder', label: 'Website Builder', category: 'Administration' },
    { path: '/administration/navigation', label: 'Navigation', category: 'Administration' },
    { path: '/administration/client/identity', label: 'Client Identity', category: 'Administration' },
    { path: '/administration/client/theme', label: 'Client Theme', category: 'Administration' },

    { path: '/content/pages', label: 'Pages', category: 'Content' },
    { path: '/content/page-sections', label: 'Page Sections', category: 'Content' },
    { path: '/content/page-layout', label: 'Page Layout', category: 'Content' },
    { path: '/content/page-builder', label: 'Page Builder', category: 'Content' },

    { path: '/people/team/members', label: 'Team Members', category: 'People' },
    { path: '/people/team/departments', label: 'Departments', category: 'People' },
    { path: '/people/clients/subscription-requests', label: 'Subscriptions', category: 'People' },
    { path: '/people/clients/email-campaigns', label: 'Email Campaigns', category: 'People' },

    { path: '/finance/payments', label: 'Payments', category: 'Finance' },

    { path: '/analytics/google/categories', label: 'Analytics Categories', category: 'Analytics' },
    { path: '/analytics/google/actions', label: 'Analytics Actions', category: 'Analytics' },

    { path: '/help', label: 'Knowledge Base', category: 'Help' },
]

export function filterSearchEntries(
    entries: SearchEntry[],
    query: string
): SearchEntry[] {
    const q = query.trim().toLowerCase()
    if (!q) return []
    const qWithSpaces = q.replace(/-/g, ' ')
    return entries.filter((e) => {
        const label = e.label.toLowerCase()
        const path = e.path.toLowerCase()
        const pathAsText = path.replace(/-/g, ' ')
        return (
            label.includes(q) ||
            label.includes(qWithSpaces) ||
            e.category.toLowerCase().includes(q) ||
            path.includes(q) ||
            pathAsText.includes(qWithSpaces)
        )
    })
}
